
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import MysteryList from "@/components/dashboard/MysteryList";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { HomeDashboard } from "@/components/dashboard/HomeDashboard";
import { useTranslation } from "react-i18next";
import { EmailVerificationBanner } from "@/components/EmailVerificationBanner";
import AttributionSurvey from "@/components/AttributionSurvey";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [mysteries, setMysteries] = useState([]);
  const [isLoadingMysteries, setIsLoadingMysteries] = useState(false);
  const [showAttribution, setShowAttribution] = useState(false);
  const { t } = useTranslation();

  // Declared before the effect (and in its deps) — previously it lived after
  // the effect, so the one-time auth listener captured the first-render
  // closure and the effect couldn't list it as a dependency.
  const fetchMysteries = useCallback(async (userId: string) => {
    setIsLoadingMysteries(true);
    try {
      const { data, error } = await supabase
        .from("conversations")
        .select("*, messages!fk_messages_conversation_id(id, content, created_at, is_ai, role)")
        .eq("user_id", userId)
        .neq("display_status", "refunded")
        .order("updated_at", { ascending: false });

      if (error) {
        throw error;
      }

      setMysteries(data || []);
    } catch (error) {
      console.error("Error fetching mysteries:", error);
      toast.error(t("dashboard.errors.loadFailed"));
    } finally {
      setIsLoadingMysteries(false);
    }
  }, [t]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Dashboard session check error:", error);
          navigate("/sign-in", { replace: true });
          return;
        }

        if (!data.session) {
          console.log("Dashboard: No active session found");
          navigate("/sign-in", { replace: true });
          return;
        }

        setUser(data.session.user);
        console.log("Dashboard: Session found for user:", data.session.user.id);

        // Fetch user's mysteries
        await fetchMysteries(data.session.user.id);

        // Check if user needs attribution survey.
        // Defer the first ask until either (a) they have at least one mystery,
        // or (b) this is at least their second visit to the dashboard. This
        // avoids high-intent buyers blowing past the prompt right after signup.
        const { data: profile } = await supabase
          .from("profiles")
          .select("attribution_source, attribution_skip_count, attribution_surveyed_at")
          .eq("id", data.session.user.id)
          .single();

        if (profile) {
          const SECOND_VISIT_KEY = "mm_dashboard_visited_v1";
          const isSecondVisit = !!localStorage.getItem(SECOND_VISIT_KEY);
          if (!isSecondVisit) localStorage.setItem(SECOND_VISIT_KEY, "1");

          let userHasMystery = false;
          try {
            const { count } = await supabase
              .from("conversations")
              .select("id", { count: "exact", head: true })
              .eq("user_id", data.session.user.id);
            userHasMystery = (count ?? 0) > 0;
          } catch { /* ignore */ }

          const eligibleToAsk = isSecondVisit || userHasMystery;

          const neverAnswered = !profile.attribution_source;
          const skippedRecently = profile.attribution_source === "skipped";
          const skipCount = profile.attribution_skip_count ?? 0;
          const lastAt = profile.attribution_surveyed_at
            ? new Date(profile.attribution_surveyed_at).getTime()
            : 0;
          const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
          const enoughTimeSinceSkip = Date.now() - lastAt > sevenDaysMs;

          const shouldShow =
            eligibleToAsk &&
            (neverAnswered ||
              (skippedRecently && skipCount < 2 && enoughTimeSinceSkip));

          if (shouldShow) setShowAttribution(true);
        }
      } catch (error) {
        console.error("Dashboard auth check error:", error);
        navigate("/sign-in", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Dashboard auth state changed:", event);
        if (event === "SIGNED_OUT") {
          navigate("/sign-in", { replace: true });
        } else if (session) {
          setUser(session.user);
          fetchMysteries(session.user.id);
        }
      }
    );

    // Clean up subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, fetchMysteries]);

  const handleCreateNewMystery = () => {
    navigate("/mystery/create");
  };

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container mx-auto py-8 px-4">
        <EmailVerificationBanner />
        {mysteries.length > 0 ? (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold">{t("dashboard.mysteries.title")}</h1>
                <p className="text-muted-foreground mt-1">
                  {t("dashboard.subtitle")}
                </p>
              </div>
              <Button onClick={handleCreateNewMystery} className="sm:self-end">
                <PlusCircle className="mr-2 h-4 w-4" />
                {t("common.buttons.createNew")}
              </Button>
            </div>
            
            {/* Mystery listing without the filter tabs */}
            <MysteryList 
              mysteries={mysteries} 
              isLoading={isLoadingMysteries}
              onRefresh={() => user && fetchMysteries(user.id)}
            />
          </div>
        ) : (
          <HomeDashboard onCreateNew={handleCreateNewMystery} />
        )}
      </main>
      <Footer />

      {user && (
        <AttributionSurvey
          open={showAttribution}
          onComplete={() => setShowAttribution(false)}
          userId={user.id}
        />
      )}
    </div>
  );
};

export default Dashboard;
